import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import ApiClient from "#/redux/api/ApiClient";
import { getErrorMessage } from "#/utils/APIUtils";

const baseUrl = "/api/sif/nacinOznacitve";
// IMPORTANT INFO:
// Could also be named as markingTypeCodeList.js since thees are intended to be used
// for animal marking types as well. (e.g. ear tag, tattoo, etc.)

const initialState = {
  list: [],
  page: {},
  byId: {},
  details: null,

  isLoading: false,
  error: null,
};

/**
 * GET /api/sif/tipIdentifikatorja
 */
export const fetchTagTypeCodeList = createAsyncThunk(
  "tagTypeCodeLists/fetchAll",
  async (_, { rejectWithValue }) => {
    // eslint-disable-next-line no-console
    console.warn("Deprecated, migrate to LookupTable hooks and components");

    try {
      const response = await ApiClient.get(baseUrl);
      return response.data || [];
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

/**
 * GET /api/sif/tipIdentifikatorja/{sifra}
 */
export const fetchTagTypeByCode = createAsyncThunk(
  "tagTypeCodeLists/fetchBySifra",
  async (sifra, { rejectWithValue }) => {
    // eslint-disable-next-line no-console
    console.warn("Deprecated, migrate to LookupTable hooks and components");

    try {
      const response = await ApiClient.get(`${baseUrl}/${sifra}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

/**
 * GET /api/sif/tipIdentifikatorja/page
 */
export const fetchTagTypeCodePage = createAsyncThunk(
  "tagTypeCodeLists/fetchPage",
  async ({ page, filter, sort } = {}, { rejectWithValue }) => {
    // eslint-disable-next-line no-console
    console.warn("Deprecated, migrate to LookupTable hooks and components");

    try {
      const response = await ApiClient.get(
        `${baseUrl}/page`,
        undefined,
        page,
        filter,
        sort,
        true
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

/**
 * POST /api/sif/tipIdentifikatorja
 */
export const createTagTypeCode = createAsyncThunk(
  "tagTypeCodeLists/create",
  async (payload, { rejectWithValue }) => {
    // eslint-disable-next-line no-console
    console.warn("Deprecated, migrate to LookupTable hooks and components");

    try {
      const response = await ApiClient.post(baseUrl, payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

/**
 * PUT /api/sif/tipIdentifikatorja/{sifra}
 */
export const updateTagTypeCode = createAsyncThunk(
  "tagTypeCodeLists/update",
  async ({ sifra, data }, { rejectWithValue }) => {
    // eslint-disable-next-line no-console
    console.warn("Deprecated, migrate to LookupTable hooks and components");

    try {
      const response = await ApiClient.put(`${baseUrl}/${sifra}`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

/**
 * DELETE /api/sif/tipIdentifikatorja/{sifra}
 */
export const deleteTagTypeCode = createAsyncThunk(
  "tagTypeCodeLists/delete",
  async (sifra, { rejectWithValue }) => {
    // eslint-disable-next-line no-console
    console.warn("Deprecated, migrate to LookupTable hooks and components");

    try {
      await ApiClient.delete(`${baseUrl}/${sifra}`);
      return sifra;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const tagTypeCodeListSlice = createSlice({
  name: "tagTypeCodeLists",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // ---- LIST (ALL) ----
    builder.addCase(fetchTagTypeCodeList.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchTagTypeCodeList.fulfilled, (state, action) => {
      state.isLoading = false;
      state.list = action.payload;

      state.byId = {};
      action.payload.forEach((item) => {
        if (item.sifra) state.byId[item.sifra] = item;
      });
    });
    builder.addCase(fetchTagTypeCodeList.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    });

    // ---- SINGLE ----
    builder.addCase(fetchTagTypeByCode.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchTagTypeByCode.fulfilled, (state, action) => {
      state.isLoading = false;
      const item = action.payload;

      if (item?.sifra) {
        state.byId[item.sifra] = item;
        state.details = item.sifra;
      }
    });
    builder.addCase(fetchTagTypeByCode.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    });

    // ---- PAGE ----
    builder.addCase(fetchTagTypeCodePage.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchTagTypeCodePage.fulfilled, (state, action) => {
      state.isLoading = false;
      state.page = action.payload;

      const content = action.payload?.content || [];
      state.list = content;
      state.byId = {};

      content.forEach((item) => {
        if (item.sifra) state.byId[item.sifra] = item;
      });
    });
    builder.addCase(fetchTagTypeCodePage.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    });

    // ---- CREATE ----
    builder.addCase(createTagTypeCode.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(createTagTypeCode.fulfilled, (state, action) => {
      state.isLoading = false;
      const item = action.payload;

      if (item.sifra) {
        state.byId[item.sifra] = item;
        state.list.push(item);
        state.details = item.sifra;
      }
    });
    builder.addCase(createTagTypeCode.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    });

    // ---- UPDATE ----
    builder.addCase(updateTagTypeCode.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(updateTagTypeCode.fulfilled, (state, action) => {
      state.isLoading = false;
      const item = action.payload;

      if (item.sifra) {
        state.byId[item.sifra] = item;

        const idx = state.list.findIndex((x) => x.sifra === item.sifra);
        if (idx !== -1) state.list[idx] = item;

        state.details = item.sifra;
      }
    });
    builder.addCase(updateTagTypeCode.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    });

    // ---- DELETE ----
    builder.addCase(deleteTagTypeCode.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(deleteTagTypeCode.fulfilled, (state, action) => {
      state.isLoading = false;
      const sifra = action.payload;

      delete state.byId[sifra];
      state.list = state.list.filter((x) => x.sifra !== sifra);

      if (state.details === sifra) state.details = null;
    });
    builder.addCase(deleteTagTypeCode.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    });
  },
});

export default tagTypeCodeListSlice.reducer;
